import {
  Box,
  BoxProps,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ResponsiveValue,
  Text,
} from '@chakra-ui/react'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import IconModalCloseX from '~/assets/svgs/icon-modal-close-x.svg?react'
import { useModalState } from '~/atoms/ModalState'
import {
  MangoModalSizes,
  useMangoModalStyles,
} from '~/components/shared/componentStyles/MangoModal.style'
import { ScrollArea } from '~/components/shared/ScrollArea'
import { Size } from '~/types/ThemeTypes'

import { QuickActionButtonVariants } from './componentStyles/QuickActionButton.style'
import { QuickActionButton } from './QuickActionButton'

/**
 * @property {string | React.ReactNode} header - The header content of the modal
 * @property {string | React.ReactNode} body - The body of the modal - can be a string or a React node - alternatively set as children if supplied.
 * @property {string | React.ReactNode} footer - The footer content of the modal
 * @property {React.ReactNode} children - The body of the modal - can be a string or a React node - alternatively set as body if supplied.  Given priority over body if both are supplied.
 */
interface MangoModalProps extends Omit<BoxProps, 'isOpen' | 'onClose'> {
  size?: ResponsiveValue<MangoModalSizes>
  isOpen?: boolean
  onClose?: () => void
  closeButton?: boolean
  header?: string | React.ReactNode
  body?: string | React.ReactNode
  footer?: string | React.ReactNode
  scrollable?: boolean
  headerDisabled?: boolean
}
export const MangoModal = ({
  size = Size.md,
  isOpen,
  onClose,
  closeButton = true,
  header,
  body,
  footer,
  scrollable,
  children,
  ...rest
}: MangoModalProps) => {
  const { setActiveModalRef } = useModalState()
  const { t } = useTranslation()
  const styles = useMangoModalStyles({
    size,
  })

  const headerContent = useMemo(() => {
    if (!header) return null
    if (typeof header === 'string') {
      return <Text sx={styles.headerText}>{header}</Text>
    }
    return header
  }, [header])

  const bodyContent = useMemo(() => {
    const innerContent = children ?? body
    const modalBody = <ModalBody sx={styles.body}>{innerContent}</ModalBody>
    if (scrollable) {
      return (
        <ScrollArea
          scrollbarOptions={{
            showOnHoverOnly: true,
          }}
        >
          {modalBody}
        </ScrollArea>
      )
    }
    return modalBody
  }, [body, children, scrollable])

  return (
    <Box
      as={Modal}
      sx={styles.dialogContainer}
      isOpen={isOpen}
      onClose={onClose}
      {...rest}
    >
      <ModalOverlay sx={styles.modalOverlay} />
      <ModalContent
        ref={(node) => {
          setActiveModalRef(node)
        }}
        sx={styles.dialog}
      >
        {(headerContent || closeButton) && (
          <ModalHeader sx={styles.header}>
            {headerContent}
            {closeButton && (
              <QuickActionButton
                onClick={onClose}
                actionIcon={IconModalCloseX}
                iconProps={{ boxSize: '0.75rem' }}
                tooltipText={t('voicemail.tooltip.close')}
                size={Size.sm}
                variant={QuickActionButtonVariants.Default_100}
              />
            )}
          </ModalHeader>
        )}
        {bodyContent}
        {footer && <ModalFooter sx={styles.footer}>{footer}</ModalFooter>}
      </ModalContent>
    </Box>
  )
}
